import { useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { EmptyState } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { ApiError } from '../../lib/api';
import { useAddSiteDay } from '../../lib/queries';
import { addDays, formatDate, formatWeekday, weekRange } from '../../lib/dates';
import type { Site } from '../../lib/types';

interface AddSiteDialogProps {
  day: string;
  sites: Site[];
  /** Baustellen, die an dem Tag schon im Plan stehen. */
  usedSiteIds: Set<number>;
  onClose: () => void;
}

/**
 * Eine Baustelle in einen Tag nehmen.
 *
 * „Übernehmen bis" ist der Alltagsfall: eine Baustelle läuft selten nur einen
 * Tag. Die Kachel wird auch dann angelegt, wenn noch niemand darauf steht —
 * eingeteilt wird danach in der Kachel selbst.
 */
export function AddSiteDialog({ day, sites, usedSiteIds, onClose }: AddSiteDialogProps) {
  const toast = useToast();
  const addSiteDay = useAddSiteDay();

  const [untilDay, setUntilDay] = useState(day);
  const [includeWeekends, setIncludeWeekends] = useState(false);
  // Feste Bereiche stehen ohnehin jeden Tag im Plan — sie tauchen hier nicht auf.
  const available = sites.filter((site) => !usedSiteIds.has(site.id) && !site.permanent);
  const isRange = untilDay > day;

  // Schnellwahl: bis Freitag (werktags) oder bis Sonntag (komplette Woche)
  // derselben Woche. Kein eigener Zustand — die Häkchen spiegeln nur, was
  // im Bis-Feld steht, damit Feld und Häkchen nie auseinanderlaufen.
  const week = weekRange(day);
  const fridayOfWeek = addDays(week.from, 4);
  const canWorkweek = fridayOfWeek > day;
  const canFullWeek = week.to > day;
  const isWorkweek = canWorkweek && untilDay === fridayOfWeek && !includeWeekends;
  const isFullWeek = canFullWeek && untilDay === week.to && includeWeekends;

  const toggleWorkweek = (checked: boolean) => {
    setUntilDay(checked ? fridayOfWeek : day);
    setIncludeWeekends(false);
  };

  const toggleFullWeek = (checked: boolean) => {
    setUntilDay(checked ? week.to : day);
    setIncludeWeekends(checked);
  };

  const choose = (site: Site) => {
    addSiteDay.mutate(
      { siteId: site.id, date: day, until: untilDay, includeWeekends },
      {
        onSuccess: (result) => {
          toast.success(
            result.created > 1
              ? `„${site.name}" für ${result.created} ${includeWeekends ? 'Tage' : 'Werktage'} eingeplant.`
              : `„${site.name}" eingeplant.`,
          );
          onClose();
        },
        onError: (error) =>
          toast.error(
            error instanceof ApiError ? error.message : 'Die Baustelle konnte nicht eingeplant werden.',
          ),
      },
    );
  };

  return (
    <Modal
      title="Baustelle einplanen"
      subtitle={`${formatWeekday(day)}, ${formatDate(day)}`}
      onClose={onClose}
      width="breit"
      className="asd-modal"
      footer={
        <button type="button" className="btn" onClick={onClose}>
          Abbrechen
        </button>
      }
    >
      {available.length === 0 ? (
        <EmptyState
          title="Alle Baustellen stehen schon in diesem Tag"
          description="Weitere Baustellen werden im Reiter „Baustellen“ angelegt."
        />
      ) : (
        <>
          <div className="sdd-range">
            <label htmlFor="asd-until">Übernehmen bis</label>
            <input
              id="asd-until"
              type="date"
              className="input"
              value={untilDay}
              min={day}
              max={addDays(day, 90)}
              onChange={(event) => {
                setUntilDay(event.target.value || day);
                setIncludeWeekends(false);
              }}
            />
            <span className="muted">
              {!isRange
                ? 'Nur dieser Tag.'
                : includeWeekends
                  ? 'Gilt für alle Tage bis dahin, auch am Wochenende.'
                  : 'Gilt für alle Werktage bis dahin.'}
            </span>

            <div className="asd-quick">
              <label className="check">
                <input
                  type="checkbox"
                  checked={isWorkweek}
                  disabled={!canWorkweek}
                  onChange={(event) => toggleWorkweek(event.target.checked)}
                />
                Ganze Woche werktags (bis Fr, {formatDate(fridayOfWeek)})
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={isFullWeek}
                  disabled={!canFullWeek}
                  onChange={(event) => toggleFullWeek(event.target.checked)}
                />
                Komplette Woche (bis So, {formatDate(week.to)})
              </label>
            </div>
          </div>

          <ul className="asd-list">
            {available.map((site) => (
              <li key={site.id}>
                <button
                  type="button"
                  className="asd-item"
                  style={{ '--site': site.color } as never}
                  disabled={addSiteDay.isPending}
                  onClick={() => choose(site)}
                >
                  <span className="asd-item-name">{site.name}</span>
                  {site.address ? <span className="asd-item-note">{site.address}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}
